import { z } from "zod";
import { formConfigToZodSchemas, getFormConfigDefaultValues } from "../form-config-to-zod-schema";
import {
  getFormKeys,
  type FormConfig,
  type FormConfigInPages,
  type I18NFunction,
  type SectionConfig,
} from "../form-config-types";
import {
  mapToNumPatientsEnumLabels,
  mapToStandardPercentageEnumLabels,
  mapToTypesOfSpecialistsEnumLabels,
  mapToTypesOfSpecialistsInvolvedInTheMDTEnumLabels,
  mapToYesNoEnumLabels,
  numPatientsEnum,
  standardPercentageEnum,
  typesOfSpecialistsEnum,
  typesOfSpecialistsInvolvedInTheMDTEnum,
  yesNoEnum,
} from "./form-data-enums";
import { enumErrorMap } from "./form-data-utils";

const ongoingManagementFormConfigInPages = (l: I18NFunction) =>
  [
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 5.1 What is your involvement in ongoing management/long term surveillance of the patient?
        // 1- Patients with surgery: [Open box]
        // 2- Patients with RT: [Open box]
        // 3- Patients with systemic IO: [Open box]
        // Is your involvement the same for CSCC and BCC? [Yes or No]
        // [If selected No] Please explain the differences [open box]
        yourInvolvementInOngoingManagementLongTermSurveillanceOfThePatient: {
          type: "object",
          label: l(
            "1. What is your involvement in ongoing management/long term surveillance of the patient?",
            "1. Quelle est votre implication dans la prise en charge continue/surveillance à long terme des patients ci-dessous?"
          ),
          properties: {
            patientsWithSurgery: {
              type: "textarea",
              label: l("Patients with surgery", "Patients ayant subi une intervention chirurgicale :"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .min(1, { message: l("Please enter a value", "Ce champ est requis") }),
            },
            patientsWithRT: {
              type: "textarea",
              label: l("Patients with RT", "Patients ayant reçu de la radiothérapie :"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .min(1, { message: l("Please enter a value", "Ce champ est requis") }),
            },
            patientsWithSystemicIO: {
              type: "textarea",
              label: l("Patients with systemic IO", "Patients ayant reçu une immunothérapie systémique :"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .min(1, { message: l("Please enter a value", "Ce champ est requis") }),
            },
            isItTheSameForCSCCAndBCC: {
              type: "radioGroup",
              label: l(
                "Is your involvement the same for advanced CSCC and BCC?",
                "Votre implication est-elle la même pour le CSCC et le BCC de stade avancé?"
              ),
              schema: z.enum(yesNoEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToYesNoEnumLabels,
              cols: 2,
            },
            pleaseExplainTheDifferences: {
              type: "textarea",
              label: l("Please explain the differences", "Veuillez expliquer les différences"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: [
                    "yourInvolvementInOngoingManagementLongTermSurveillanceOfThePatient.isItTheSameForCSCCAndBCC",
                    "not-equals",
                    "No",
                  ],
                  then: "hide",
                },
                {
                  if: [
                    "yourInvolvementInOngoingManagementLongTermSurveillanceOfThePatient.isItTheSameForCSCCAndBCC",
                    "equals",
                    "No",
                  ],
                  then: "require",
                },
              ],
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 5.2 How do you track and manage disease progression or recurrence? [open box]
        howDoYouTrackAndManageDiseaseProgressionOrRecurrence: {
          type: "textarea",
          label: l(
            "2. How do you track and manage disease progression or recurrence?",
            "2. Comment suivez-vous et prenez-vous en charge la progression ou la récidive de la maladie?"
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
              invalid_type_error: l("Please enter a value", "Ce champ est requis"),
            })
            .min(1, { message: l("Please enter a value", "Ce champ est requis") }),
        },

        isItTheSameForAdvancedCSCCBCC: {
          type: "radioGroup",
          label: l("Is it the same for advanced CSCC and BCC?", "Est-ce le même pour les CSCC et les BCC avancés?"),
          schema: z.enum(yesNoEnum, {
            errorMap: enumErrorMap(l),
          }),
          mapToLabels: mapToYesNoEnumLabels,
          cols: 2,
        },

        pleaseExplainDifferences: {
          type: "textarea",
          label: l("Please explain the differences", "Veuillez expliquer les différences"),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
              invalid_type_error: l("Please enter a value", "Ce champ est requis"),
            })
            .nullish(),
          dependencies: [
            {
              if: ["isItTheSameForAdvancedCSCCBCC", "not-equals", "No"],
              then: "hide",
            },
            {
              if: ["isItTheSameForAdvancedCSCCBCC", "equals", "No"],
              then: "require",
            },
          ],
        },

        // 5.3 Who is most closely involved in surveillance of patients and follow up? [open box]
        whoIsMostCloselyInvolvedInSurveillanceOfPatientsAndFollowUp: {
          type: "textarea",
          label: l(
            "3. Who is most closely involved in surveillance of patients and follow up?",
            "3. Qui participe le plus étroitement à la surveillance des patients et au suivi?"
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
              invalid_type_error: l("Please enter a value", "Ce champ est requis"),
            })
            .min(1, { message: l("Please enter a value", "Ce champ est requis") }),
        },

        // 5.4 Are there gaps in post treatment follow up that need improvement? [open box]
        areThereGapsInPostTreatmentFollowUpThatNeedImprovement: {
          type: "textarea",
          label: l(
            "4. Are there gaps in post treatment follow up that need improvement?",
            "4. Y a-t-il des lacunes dans le suivi post-traitement qui doivent être améliorées?"
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
              invalid_type_error: l("Please enter a value", "Ce champ est requis"),
            })
            .min(1, { message: l("Please enter a value", "Ce champ est requis") }),
        },
      },
    },
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        ongoingDoYouHaveAnyAdditionalComments: {
          type: "textarea",
          label: l(
            "Do you have any additional comments, suggestions, or concerns regarding the current version of the schema, particularly in relation to your own personal practice?",
            "Avez-vous d'autres commentaires, suggestions ou préoccupations au sujet de la version actuelle du schéma, particulièrement en ce qui concerne votre pratique personnelle?"
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
              invalid_type_error: l("Please enter a value", "Ce champ est requis"),
            })
            .nullish(),
        },
      },
    },
  ] as const as FormConfigInPages;

export const ongoingManagementForm: (l: I18NFunction) => SectionConfig = (l) => ({
  sectionId: "ongoing-management",
  sectionNumber: 5,
  title: l("Ongoing Management", "Gestion continue"),
  description: l("Ongoing Management", "Gestion continue"),
  diagrams: [
    {
      src: l("/diagrams/ongoing_management.png", "/diagrams/ongoing_management_fr.png"),
      alt: l("Ongoing Management", "Gestion continue"),
    },
  ],
  formPages: ongoingManagementFormConfigInPages(l).map((page) => ({
    formConfig: page.formFields,
    formSchemas: formConfigToZodSchemas(page.formFields, l),
    defaultValues: getFormConfigDefaultValues(page.formFields),
  })),
  // formRootKeys: Object.keys(
  //   Object.assign({}, ...ongoingManagementFormConfigInPages.flatMap((page) => page.formFields))
  // ),
  responseKeyToFormConfig: Object.fromEntries(
    ongoingManagementFormConfigInPages(l).flatMap((page) =>
      Object.entries(page.formFields).map(([key, field]) => [key, field])
    )
  ),
});
